import { computed, reactive } from 'vue';
import userFields from '@/stores/Checkout/fields';
import axios from 'axios';
import usePayment from '@/stores/Payment';
import useBasket from '@/stores/Basket';
import useBonusCode from '../BonusCode';
import useProduct from '../Product';
import Helper from '@/libraries/Helpers';

const httpInstance = axios.create();

const state = reactive({
  userFields: userFields()
});

const { getSettings } = useBasket();

export default function useCheckout() {
  const getFilteredUserFields = () => {
    return computed(() => {
      const collect = [];
      let isLegal = false;
      const termsHeading = getSettings('termsHeading');
      const termsLink = getSettings('termsLink');

      const findRecipientIndex = state.userFields.findIndex(
        field => field.name === 'recipient'
      );

      if (findRecipientIndex > -1) {
        const recipient = state.userFields[findRecipientIndex];
        isLegal = recipient.value === 'jur';
      }

      state.userFields.forEach(field => {
        if (
          field.visibility === 'default' ||
          (field.visibility === 'legal' && isLegal) ||
          (field.visibility === 'agreeTerms' && termsHeading && termsLink)
        ) {
          if (field.visibility === 'agreeTerms') {
            field.label = termsHeading;
            field.link = termsLink;
          }
          field.required = field.rules && field.rules.includes('required');
          collect.push(field);
        } else {
          field.error = false;
        }
      });

      return collect;
    }).value;
  };

  const fillUserData = data => {
    state.userFields.forEach(field => {
      if (data[field.name]) {
        field.value = data[field.name];
      }
    });
  };

  const saveClientData = async () => {
    try {
      const collectData = {};
      const fields = getFilteredUserFields();
      fields.forEach(field => {
        if (field.name !== 'agree_terms') {
          collectData[field.name] = field.value || null;
        }
      });
      await httpInstance.post('get/api/shopBasket/clientData', {
        data: collectData
      });
    } catch (error) {
      console.error(error);
    }
  };

  const goToBank = async () => {
    const { getPaymentMethodKey } = usePayment();
    const paymentMethodKey = getPaymentMethodKey();
    const { getBonusCode } = useBonusCode();
    const { getProducts } = useProduct();

    if (!paymentMethodKey) {
      return false;
    }

    const data = {};
    if (paymentMethodKey === 'invoice') {
      data['invoice'] = true;
    } else {
      data['bank'] = paymentMethodKey;
    }

    try {
      const {
        data: { success, redirect }
      } = await httpInstance.post('get/api/shopBasket/checkout', data);

      if (success) {
        const dataProducts = [];
        let totalPriceWithVat = 0;

        getProducts().forEach((product, productIndex) => {
          const priceWithVat = Helper.roundPrice(
            Helper.vatPrice(product.price, product.vat)
          );
          let discountPriceWithVat = null;
          if (product.discountPrice) {
            discountPriceWithVat =
              priceWithVat -
              Helper.roundPrice(
                Helper.vatPrice(product.discountPrice, product.vat)
              );
          }
          if (discountPriceWithVat !== null) {
            totalPriceWithVat += discountPriceWithVat * product.amount;
          } else {
            totalPriceWithVat += priceWithVat * product.amount;
          }

          let categoryName = '';

          if (product.categories.length > 0) {
            categoryName = product.categories[0].name;
          }

          dataProducts.push({
            index: productIndex,
            item_id: product.productCode || product.id,
            item_name: product.productName,
            item_brand: '',
            item_category: categoryName,
            price: priceWithVat,
            discount: discountPriceWithVat || 0,
            quantity: product.amount
          });
        });

        if (typeof window.dataLayer === 'undefined') {
          window.dataLayer = [];
        }

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'begin_checkout',
          ecommerce: {
            currency: 'EUR',
            value: totalPriceWithVat,
            items: dataProducts,
            coupon: getBonusCode()
          }
        });

        await new Promise(resolve => setTimeout(resolve, 10));

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            currency: 'EUR',
            value: totalPriceWithVat,
            items: dataProducts,
            coupon: getBonusCode(),
            transaction_id: String(getSettings('id'))
          }
        });

        await new Promise(resolve => setTimeout(resolve, 50));
        if (redirect) {
          window.location.href = redirect;
          return {
            redirecting: true,
            success
          };
        } else {
          return {
            redirecting: false,
            success
          };
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return {
    getFilteredUserFields,
    saveClientData,
    fillUserData,
    goToBank
  };
}
